import React, { useEffect } from "react";
import { toast } from "sonner";

/**
 * Listens for the global `eip:session-reset` event emitted by the axios
 * interceptor whenever the backend sends `X-Session-Reset`. Surfaces a Sonner
 * toast so a returning user immediately understands why the tab was reset
 * (rather than the ghosty "just kicked to /login" experience).
 *
 * Because the interceptor typically also triggers a hard redirect to /login,
 * we persist the reason in sessionStorage and drain it on mount. That way the
 * toast reliably shows on the freshly-loaded /login page.
 */
const RESET_KEY = "eip:pending-reset";
let _lastToastAt = 0;

function showResetToast(reason) {
  const now = Date.now();
  // Dedupe by reason within a short window so a burst of parallel 401s from
  // the stale-cookie sweep only produces one toast.
  if (now - _lastToastAt < 3000) return;
  _lastToastAt = now;
  const message = reason === "stale-cookie"
    ? "Your previous session was invalid and has been cleared. Please sign in again."
    : "Your session was reset by the server.";
  toast.warning(message, { duration: 6000, id: "session-reset" });
}

export function SessionResetBanner() {
  useEffect(() => {
    // Drain any pending reset stored before a hard redirect.
    try {
      const pending = sessionStorage.getItem(RESET_KEY);
      if (pending) {
        sessionStorage.removeItem(RESET_KEY);
        // Defer to next paint so Sonner is fully mounted.
        setTimeout(() => showResetToast(pending), 50);
      }
    } catch { /* sessionStorage disabled — no-op */ }

    const onReset = (ev) => {
      const reason = ev?.detail?.reason || "session-reset";
      showResetToast(reason);
    };
    window.addEventListener("eip:session-reset", onReset);
    return () => window.removeEventListener("eip:session-reset", onReset);
  }, []);
  return null;
}
